import type { OwnerListQuery, OwnerListPage, ProductListRow, PostListRow } from "@stackje/site-builder";
import type { BlogPostEntry, ShopProductEntry, ShopIndex } from "./contract";
export class OwnerListRequestError extends Error {
  constructor(message: string, readonly status: number) { super(message); }
}
export type PostRow = PostListRow & { coverSrc?: string };
export interface ProductDetail { product: ShopProductEntry; stock: ShopIndex["stock"]; files: ShopIndex["files"] }
export interface OwnerListsApi {
  products(siteId: string, query?: OwnerListQuery, signal?: AbortSignal): Promise<OwnerListPage<ProductListRow>>;
  posts(siteId: string, query?: OwnerListQuery, signal?: AbortSignal): Promise<OwnerListPage<PostRow>>;
  product(siteId: string, id: string, signal?: AbortSignal): Promise<ProductDetail>;
  post(siteId: string, id: string, signal?: AbortSignal): Promise<BlogPostEntry>;
}
export function createHttpOwnerListsApi(fetcher: typeof fetch = (...args) => fetch(...args)): OwnerListsApi {
  const root = (site: string) => `/_builder/sites/${encodeURIComponent(site)}`;
  async function read<T>(path: string, signal?: AbortSignal): Promise<T> {
    const response = await fetcher(path, { credentials: "same-origin", cache: "no-store", signal });
    const value = await response.json() as T & { message?: string; error?: string };
    if (!response.ok) throw new OwnerListRequestError(value.message ?? value.error ?? "The list couldn't be loaded. Try again.", response.status);
    return value;
  }
  const params = (q: OwnerListQuery = {}) => new URLSearchParams(Object.entries(q).filter(([,v]) => v !== undefined).map(([k,v]) => [k,String(v)]));
  return {
    products: (site, query, signal) => read(`${root(site)}/shop/products?${params(query)}`, signal),
    posts: (site, query, signal) => read(`${root(site)}/blog/posts?${params(query)}`, signal),
    product: (site, id, signal) => read(`${root(site)}/shop/products/${encodeURIComponent(id)}`, signal),
    post: async (site, id, signal) => (await read<{ post: BlogPostEntry }>(`${root(site)}/blog/posts/${encodeURIComponent(id)}`, signal)).post,
  };
}
